"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getStoredTheme,
  getSystemTheme,
  setTheme as applyThemePreference,
  subscribeToThemeChanges,
  type Theme,
} from "@/lib/theme";
import {
  ACTIVE_KNOWLEDGE_EVENT,
  ACTIVE_KNOWLEDGE_STORAGE_KEY,
  ACTIVE_SESSION_EVENT,
  ACTIVE_SESSION_STORAGE_KEY,
  LANGUAGE_EVENT,
  LANGUAGE_STORAGE_KEY,
  SIDEBAR_COLLAPSED_EVENT,
  SIDEBAR_COLLAPSED_STORAGE_KEY,
  normalizeLanguage,
  readStoredActiveSessionId,
  readStoredActiveKnowledge,
  readStoredLanguage,
  readStoredLanguageRaw,
  readStoredRecentKnowledge,
  readStoredSidebarCollapsed,
  writeStoredRecentKnowledge,
  RECENT_KNOWLEDGE_LIMIT,
  writeStoredActiveSessionId,
  writeStoredActiveKnowledge,
  writeStoredLanguage,
  writeStoredSidebarCollapsed,
  type AppLanguage,
} from "@/context/app-shell-storage";
import { apiFetch, apiUrl } from "@/lib/api";

interface AppShellContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  /** 서버 값 채택 전용. 서버로 되돌려 보내지 않는다. */
  syncLanguageFromServer: (language: AppLanguage) => void;
  activeSessionId: string | null;
  setActiveSessionId: (sessionId: string | null) => void;
  activeKnowledge: string | null;
  setActiveKnowledge: (name: string | null) => void;
  /** Books opened most recently first — feeds the sidebar's "recent books" mode. */
  recentKnowledge: string[];
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

const AppShellContext = createContext<AppShellContextValue | null>(null);

export function AppShellProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    return getStoredTheme() ?? getSystemTheme();
  });
  // Always start with "en" to match SSR; hydrate from localStorage after mount
  const [language, setLanguageState] = useState<AppLanguage>("ko");
  const [activeSessionId, setActiveSessionIdState] = useState<string | null>(
    () => readStoredActiveSessionId(),
  );
  const [activeKnowledge, setActiveKnowledgeState] = useState<string | null>(
    () => readStoredActiveKnowledge(),
  );
  // Always start expanded to match SSR; hydrate from localStorage after mount
  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(false);
  const [recentKnowledge, setRecentKnowledgeState] = useState<string[]>([]);

  useEffect(() => {
    // Hydrate client-only preferences after SSR-safe first render.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLanguageState(readStoredLanguage());
    setSidebarCollapsedState(readStoredSidebarCollapsed());
    setRecentKnowledgeState(readStoredRecentKnowledge());
  }, []);

  useEffect(() => {
    return subscribeToThemeChanges((nextTheme) => {
      setThemeState(nextTheme);
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onStorage = (event: StorageEvent) => {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        setLanguageState(normalizeLanguage(event.newValue));
      }
      if (event.key === ACTIVE_SESSION_STORAGE_KEY) {
        setActiveSessionIdState(event.newValue);
      }
      if (event.key === ACTIVE_KNOWLEDGE_STORAGE_KEY) {
        setActiveKnowledgeState(event.newValue);
      }
      if (event.key === SIDEBAR_COLLAPSED_STORAGE_KEY) {
        setSidebarCollapsedState(event.newValue === "1");
      }
    };

    const onLanguage = (event: Event) => {
      const detail = (event as CustomEvent<{ language?: AppLanguage }>).detail;
      setLanguageState(normalizeLanguage(detail?.language));
    };

    const onActiveSession = (event: Event) => {
      const detail = (event as CustomEvent<{ sessionId?: string | null }>)
        .detail;
      setActiveSessionIdState(detail?.sessionId ?? null);
    };
    const onActiveKnowledge = (event: Event) => {
      const detail = (event as CustomEvent<{ name?: string | null }>).detail;
      setActiveKnowledgeState(detail?.name ?? null);
    };


    const onSidebarCollapsed = (event: Event) => {
      const detail = (event as CustomEvent<{ collapsed?: boolean }>).detail;
      setSidebarCollapsedState(Boolean(detail?.collapsed));
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener(LANGUAGE_EVENT, onLanguage);
    window.addEventListener(ACTIVE_SESSION_EVENT, onActiveSession);
    window.addEventListener(ACTIVE_KNOWLEDGE_EVENT, onActiveKnowledge);
    window.addEventListener(SIDEBAR_COLLAPSED_EVENT, onSidebarCollapsed);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(LANGUAGE_EVENT, onLanguage);
      window.removeEventListener(ACTIVE_SESSION_EVENT, onActiveSession);
      window.removeEventListener(SIDEBAR_COLLAPSED_EVENT, onSidebarCollapsed);
      window.removeEventListener(ACTIVE_KNOWLEDGE_EVENT, onActiveKnowledge);
    };
  }, []);

  const setTheme = useCallback((nextTheme: Theme) => {
    applyThemePreference(nextTheme);
    setThemeState(nextTheme);
  }, []);

  // 언어 SSOT — 로컬 저장 + 서버 영속을 여기서 한 번에 처리한다.
  // SettingsContext.updateLanguage 는 이 함수에 위임하므로 영속 경로가 하나뿐이다.
  // (SettingsProvider 는 /settings/* 전용이라 전역 사이드바에서 쓸 수 없다.)
  const setLanguage = useCallback(
    (nextLanguage: AppLanguage) => {
      writeStoredLanguage(nextLanguage);
      setLanguageState(nextLanguage);
      void apiFetch(apiUrl("/api/v1/settings/ui"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ theme, language: nextLanguage }),
      }).catch(() => {
        // 서버 영속 실패는 로컬 전환을 막지 않는다 — 다음 변경에서 다시 시도된다.
      });
    },
    [theme],
  );

  // 서버 설정 하이드레이션 전용 — 로컬 상태만 맞추고 PUT 을 되돌려 보내지 않는다.
  // 이게 없으면 설정 불러오기→영속→불러오기 피드백 루프가 생긴다.
  const syncLanguageFromServer = useCallback((nextLanguage: AppLanguage) => {
    // 로컬에 저장된 선택이 있으면 그게 이긴다 — 사용자가 이 브라우저에서 고른 값을
    // 뒤늦게 도착한 서버 응답이 덮어쓰면 방금 누른 토글이 되돌아가 버린다.
    if (readStoredLanguageRaw() !== null) return;
    writeStoredLanguage(nextLanguage);
    setLanguageState(nextLanguage);
  }, []);

  const setActiveSessionId = useCallback((sessionId: string | null) => {
    writeStoredActiveSessionId(sessionId);
    setActiveSessionIdState(sessionId);
  }, []);
  const setActiveKnowledge = useCallback((name: string | null) => {
    writeStoredActiveKnowledge(name);
    setActiveKnowledgeState(name);
    if (!name) return;
    setRecentKnowledgeState((prev) => {
      const next = [name, ...prev.filter((item) => item !== name)].slice(
        0,
        RECENT_KNOWLEDGE_LIMIT,
      );
      writeStoredRecentKnowledge(next);
      return next;
    });
  }, []);


  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    writeStoredSidebarCollapsed(collapsed);
    setSidebarCollapsedState(collapsed);
  }, []);

  const value = useMemo<AppShellContextValue>(
    () => ({
      theme,
      setTheme,
      language,
      setLanguage,
      syncLanguageFromServer,
      activeSessionId,
      setActiveSessionId,
      activeKnowledge,
      setActiveKnowledge,
      recentKnowledge,
      sidebarCollapsed,
      setSidebarCollapsed,
    }),
    [
      activeKnowledge,
      recentKnowledge,
      activeSessionId,
      language,
      syncLanguageFromServer,
      setActiveSessionId,
      setActiveKnowledge,
      setLanguage,
      setSidebarCollapsed,
      setTheme,
      sidebarCollapsed,
      theme,
    ],
  );

  return (
    <AppShellContext.Provider value={value}>
      {children}
    </AppShellContext.Provider>
  );
}

export function useAppShell() {
  const context = useContext(AppShellContext);
  if (!context) {
    throw new Error("useAppShell must be used inside AppShellProvider");
  }
  return context;
}
